import { useMutation } from '@tanstack/react-query';
import ObrasApi from 'Data/Obras.api';
import toastHandler from 'Utils/toastHandler';
import { FILES_QUERY_KEY, QUERY_KEY_MOVE_FILES_LIST } from 'Data/keys/QueryKeys';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { queryClient } from 'Context/QueryClientContext';
import { IUnarchiveFilesRequest } from 'Data/interfaces/Obra/IUnarchiveFilesRequest';

export const useQueryUnarchiveFiles = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IUnarchiveFilesRequest) => {
      return ObrasApi.unarchiveFiles(variables);
    },
    onMutate: async (variables) => {
      const previousObsoleteFiles = queryClient.getQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.ObsoleteFolderId],
      );
      const previousMoveObsoleteFiles = queryClient.getQueryData<IUseQueryFilesReturn>([
        QUERY_KEY_MOVE_FILES_LIST,
        Number(variables.ConstructionSiteId),
        variables.ObsoleteFolderId,
      ]);

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.ObsoleteFolderId],
        (old) => {
          if (old) {
            if (old.ListFiles.length > 0) {
              return {
                ...old,
                ListFiles: old.ListFiles.filter((file) => {
                  return !variables.FilesId.includes(file.Identifier.ApiId);
                }),
              };
            }
            return { ...old };
          }
          return old;
        },
      );
      queryClient.setQueryData<IUseQueryFilesReturn>(
        [
          QUERY_KEY_MOVE_FILES_LIST,
          Number(variables.ConstructionSiteId),
          variables.ObsoleteFolderId,
        ],
        (old) => {
          if (old) {
            if (old.ListFiles.length > 0) {
              return {
                ...old,
                ListFiles: old.ListFiles.filter((file) => {
                  return !variables.FilesId.includes(file.Identifier.ApiId);
                }),
              };
            }
            return { ...old };
          }
          return old;
        },
      );

      return { previousObsoleteFiles, previousMoveObsoleteFiles };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.FolderId],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEY_MOVE_FILES_LIST,
          Number(variables.ConstructionSiteId),
          variables.FolderId,
        ],
        exact: true,
      });
      toastHandler.showSuccess('Desarquivado com sucesso');
    },
    onError: async (error: any, variables, context) => {
      await Promise.all([
        await queryClient.cancelQueries({
          queryKey: [
            FILES_QUERY_KEY,
            Number(variables.ConstructionSiteId),
            variables.ObsoleteFolderId,
          ],
        }),
        await queryClient.cancelQueries({
          queryKey: [
            QUERY_KEY_MOVE_FILES_LIST,
            Number(variables.ConstructionSiteId),
            variables.ObsoleteFolderId,
          ],
        }),
      ]);

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.ObsoleteFolderId],
        context?.previousObsoleteFiles,
      );
      queryClient.setQueryData<IUseQueryFilesReturn>(
        [
          QUERY_KEY_MOVE_FILES_LIST,
          Number(variables.ConstructionSiteId),
          variables.ObsoleteFolderId,
        ],
        context?.previousMoveObsoleteFiles,
      );

      const msg = 'Erro ao desarquivar arquivo';
      const errorMsg = error.errors ? error.errors[0]?.Message || msg : msg;
      toastHandler.showError(errorMsg);
    },
  });

  return {
    unarchiveFiles: mutate,
    isLoading: isPending,
  };
};
